import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import startCase from 'lodash/startCase';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';
import ToastContext from 'contexts/ToastContext';
import useCurrentAccountId from 'hooks/useCurrentAccountId';

import { BonusOverviewContext } from '../BonusOverviewProvider';
import useHiddenOverrideRewardNames from '../../hooks/useHiddenOverrideRewardNames';
import fetchWeeklyRewardRecords from '../../utils/fetchWeeklyRewardRecords';
import getDriverOverrides from '../../api/getDriverOverrides';
import getRewardOverrideOptions from '../../api/getRewardOverrideOptions';
import putBonusPreview from '../../api/putBonusPreview';
import buildOverrideState, {
  hasAnyOverride,
  toOverridesRequest,
} from '../../utils/buildOverrideState';
import putDriverOverrides from '../../api/putDriverOverrides';
import deleteDriverOverrides from '../../api/deleteDriverOverrides';

export interface WeekOverrideState {
  bonusDatasetRecordId: string;
  startDate: string;
  endDate: string;
  label: string;
  actual: BonusTypes.Rewards.AnyDriverRewardsOutput;
  // Persisted non-reward overrides for the week, echoed on confirm so the full
  // REPLACE PUT does not wipe them. Reward-level persisted overrides come from
  // `actual.rewardOverrides`.
  persisted: BonusTypes.Overrides.Persisted | undefined;
  preview: BonusTypes.Rewards.AnyDriverRewardsOutput | undefined;
  options: Map<string, BonusTypes.RewardOverrideOptions>;
  staged: BonusTypes.Overrides.UserInputRewardOverride[];
  // Staged eligibility (qualifier) override for the week, set via the
  // `qualifiesForBonus` column. Independent of the reward overrides above.
  stagedQualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
  persistedQualifier: BonusTypes.Overrides.PersistedQualifier | undefined;
  // Persisted criteria overrides (by `criterionField`) staged for removal.
  stagedCriteriaClears: string[];
  optionsFailed: boolean;
  isPreviewLoading: boolean;
  // The week's actual (persisted) eligibility.
  isQualified: boolean;
  // Eligibility after the staged ⊕ persisted qualifier override — reward cells
  // gate on this, so staging a week to Qualified unlocks its pay dropdowns.
  isEffectivelyQualified: boolean;
}

export interface WeeklyConfirmResult {
  ok: boolean;
  total: number;
  succeeded: number;
  failed: { startDate: string; label: string }[];
}

// A single reward line in the confirm dialog's per-week breakdown. `actual`/
// `preview` are raw reward values (the dialog formats them with `field`, the
// reward's own dataset field). `isChanged` is false for rewards the override
// doesn't affect — those are listed with just their current value.
export interface WeeklyPayChange {
  rewardName: string;
  label: string;
  field: string | undefined;
  actual: number;
  preview: number;
  isChanged: boolean;
}

export interface WeeklyWeekBreakdown {
  startDate: string;
  label: string;
  changes: WeeklyPayChange[];
}

interface WeeklyBonusOverrideContextType {
  isLoading: boolean;
  weeks: WeekOverrideState[];
  byRecordId: Map<string, WeekOverrideState>;
  stageWeekReward: (args: {
    bonusDatasetRecordId: string;
    rewardName: string;
    override: BonusTypes.Overrides.UserInputRewardOverride | undefined;
  }) => void;
  stageWeekQualifier: (args: {
    bonusDatasetRecordId: string;
    qualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
  }) => void;
  stageWeekCriteriaClear: (args: {
    bonusDatasetRecordId: string;
    criterionField: string;
    isCleared: boolean;
  }) => void;
  changedWeekCount: number;
  stagedOverrideCount: number;
  cumulativeOverrideCount: number;
  // Persisted overrides staged for removal (reward/eligibility/criteria Clears).
  stagedRemovalCount: number;
  isAnyPreviewLoading: boolean;
  aggregatePayChange: { actualPay: number; previewPay: number } | undefined;
  // Per-week breakdown of the account's override-visible rewards (changed and
  // unchanged) for every changed week.
  breakdown: WeeklyWeekBreakdown[];
  isNoteRequired: boolean;
  onWeeklyOverridesConfirmed: (
    note: string,
    reasonCodes: string[],
  ) => Promise<WeeklyConfirmResult>;
  bonusProgram: BonusTypes.BonusProgram | undefined;
}

export const WeeklyBonusOverrideContext =
  React.createContext<WeeklyBonusOverrideContextType>({
    isLoading: false,
    weeks: [],
    byRecordId: new Map(),
    stageWeekReward: window.tokenFunction,
    stageWeekQualifier: window.tokenFunction,
    stageWeekCriteriaClear: window.tokenFunction,
    changedWeekCount: 0,
    stagedOverrideCount: 0,
    cumulativeOverrideCount: 0,
    stagedRemovalCount: 0,
    isAnyPreviewLoading: false,
    aggregatePayChange: undefined,
    breakdown: [],
    isNoteRequired: false,
    onWeeklyOverridesConfirmed: () =>
      Promise.resolve({ ok: true, total: 0, succeeded: 0, failed: [] }),
    bonusProgram: undefined,
  });

// The full desired reward-override set for a week = persisted (on the record) ⊕
// staged (Sets applied, Clears removed). Used for the override counts; the
// preview/confirm bodies come from buildWeekOverrideState.
const buildEffectiveRewardOverrides = (
  persistedRewards: BonusTypes.Rewards.AnyDriverRewardsOutput['rewardOverrides'],
  staged: BonusTypes.Overrides.UserInputRewardOverride[],
): BonusTypes.Overrides.UnifiedRewardOverride[] => {
  const byName = new Map<string, BonusTypes.Overrides.UnifiedRewardOverride>();
  (persistedRewards ?? []).forEach((o) => {
    byName.set(o.rewardName, {
      rewardName: o.rewardName,
      rate: o.rate,
      amount: o.amount,
    });
  });
  staged.forEach((s) => {
    if (s.action === 'Clear') {
      byName.delete(s.rewardName);
    } else {
      byName.set(s.rewardName, {
        rewardName: s.rewardName,
        rate: s.rate,
        amount: s.amount,
      });
    }
  });
  return Array.from(byName.values());
};

// The week's desired eligibility (qualifier) override = staged (Set → value,
// Clear → none) layered over the persisted one. Used for the counts and the
// week's effective eligibility.
const buildEffectiveQualifier = ({
  persistedQualifier,
  stagedQualifier,
}: {
  persistedQualifier: BonusTypes.Overrides.PersistedQualifier | undefined;
  stagedQualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
}): BonusTypes.Overrides.UnifiedQualifierOverride | undefined => {
  if (stagedQualifier) {
    return stagedQualifier.action === 'Clear'
      ? undefined
      : { value: stagedQualifier.value };
  }
  return persistedQualifier ? { value: persistedQualifier.value } : undefined;
};

// A reward's value on a reward output, looking through nested sub-rewards (e.g.
// an aggregated Fuel reward's mpg/idle children). Undefined when absent.
const findRewardValue = (
  rewards: BonusTypes.Rewards.AnyRewardOutput[],
  rewardName: string,
): number | undefined => {
  const topLevel = rewards.find((r) => r.name === rewardName);
  if (topLevel) {
    return topLevel.reward ?? undefined;
  }
  for (const r of rewards) {
    const sub = (r.rewards ?? []).find((c) => c.name === rewardName);
    if (sub) {
      return sub.reward ?? undefined;
    }
  }
  return undefined;
};

// A week's complete desired override state (persisted ⊕ staged, every kind) —
// the body for both the preview and the confirm's REPLACE. Persisted reward
// overrides are on the weekly record; the other kinds come from GET /overrides.
const buildWeekOverrideState = ({
  week,
  staged,
  stagedQualifier,
  stagedCriteriaClears,
}: {
  week: BaseWeek;
  staged: BonusTypes.Overrides.UserInputRewardOverride[];
  stagedQualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
  stagedCriteriaClears: string[];
}): BonusTypes.Overrides.OverrideState =>
  buildOverrideState({
    persisted: week.persisted,
    persistedRewardOverrides: week.actual.rewardOverrides,
    stagedCriteria: stagedCriteriaClears.map(
      (criterionField): BonusTypes.Overrides.UserInputCriteria => ({
        action: 'Clear',
        criterionField,
      }),
    ),
    stagedQualifier,
    stagedRewardOverrides: staged,
  });

interface BaseWeek {
  bonusDatasetRecordId: string;
  startDate: string;
  endDate: string;
  label: string;
  actual: BonusTypes.Rewards.AnyDriverRewardsOutput;
  persisted: BonusTypes.Overrides.Persisted | undefined;
  options: Map<string, BonusTypes.RewardOverrideOptions>;
  optionsFailed: boolean;
  isQualified: boolean;
}

/**
 * Per-week override state for sub-period bonus programs, powering the fact-table
 * driven override flow (each week is its own record → its own options + preview +
 * PUT). Mounted inside BonusOverviewProvider so it reads `currentMode`; it only
 * loads its weekly data while override mode is active, and is otherwise inert
 * (default-mode fact tables keep reading Elastic docs). Confirm fans out one
 * override write per changed week, sharing a single note + reason codes.
 */
const WeeklyBonusOverrideProvider = ({
  driver,
  bonusProgramId,
  onOverridesSaved,
  children,
}: {
  driver: string;
  bonusProgramId: string;
  onOverridesSaved?: () => void;
  children: JSX.Element | JSX.Element[];
}) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { showToast } = useContext(ToastContext);
  const { currentMode, exitOverrideMode } = useContext(BonusOverviewContext);
  const accountId = useCurrentAccountId();
  const hiddenRewardNames = useHiddenOverrideRewardNames({ bonusProgramId });

  const isActiveMode = currentMode.mode === 'override';
  const intervalId = selectedBonusPeriod?.id;

  const bonusProgram = useMemo(
    () => bonusPrograms.find((p) => p.id === bonusProgramId),
    [bonusPrograms, bonusProgramId],
  );

  // Only BonusTypes.BonusPeriod carries sub-period windows; narrow the union.
  const subPeriods = useMemo<Period[]>(
    () =>
      selectedBonusPeriod && 'subPeriods' in selectedBonusPeriod
        ? selectedBonusPeriod.subPeriods ?? []
        : [],
    [selectedBonusPeriod],
  );

  // A key identifying the data the current override session needs. Deriving
  // `isLoading` from it (rather than a flag toggled inside the effect) means the
  // provider reports loading in the SAME render override mode is entered, with no
  // pre-effect frame where the cells flash their plain values.
  //
  // Only periods that carry weekly windows need this data, so the provider stays
  // inert (no Firestore read, no per-week API calls, never loading) for existing
  // single-window programmes even though it is mounted on every bonus slideout.
  const hasSubPeriods = subPeriods.length > 0;
  const loadKey =
    isActiveMode && intervalId && hasSubPeriods
      ? `${driver}|${bonusProgramId}|${intervalId}`
      : undefined;
  const [loadedKey, setLoadedKey] = useState<string | undefined>();
  const isLoading = !!loadKey && loadedKey !== loadKey;

  const [baseWeeks, setBaseWeeks] = useState<BaseWeek[]>([]);
  const [stagedByRecord, setStagedByRecord] = useState<
    Record<string, BonusTypes.Overrides.UserInputRewardOverride[]>
  >({});
  const [stagedQualifierByRecord, setStagedQualifierByRecord] = useState<
    Record<string, BonusTypes.Overrides.UserInputQualifier>
  >({});
  const [stagedCriteriaClearsByRecord, setStagedCriteriaClearsByRecord] =
    useState<Record<string, string[]>>({});
  const [previewByRecord, setPreviewByRecord] = useState<
    Record<string, BonusTypes.Rewards.AnyDriverRewardsOutput | undefined>
  >({});
  const [previewLoadingByRecord, setPreviewLoadingByRecord] = useState<
    Record<string, boolean>
  >({});

  // Load all weekly records + per-week persisted overrides + options, but only
  // once override mode is entered (default mode never needs this data).
  useEffect(() => {
    if (!isActiveMode || !intervalId || !hasSubPeriods) {
      return;
    }

    let isActive = true;
    setLoadedKey(undefined);
    setBaseWeeks([]);
    setStagedByRecord({});
    setStagedQualifierByRecord({});
    setStagedCriteriaClearsByRecord({});
    setPreviewByRecord({});
    setPreviewLoadingByRecord({});

    const load = async (): Promise<BaseWeek[]> => {
      const records = await fetchWeeklyRewardRecords({
        accountId,
        driver,
        bonusProgramId,
        bonusPeriodId: intervalId,
      });

      return Promise.all(
        records.map(async (actual): Promise<BaseWeek> => {
          const bonusDatasetRecordId = actual.bonusDatasetRecordId;
          const [overrideResponse, optionsResponse] = await Promise.all([
            getDriverOverrides({ api, bonusDatasetRecordId }),
            getRewardOverrideOptions({ api, bonusDatasetRecordId }),
          ]);

          const options = new Map<string, BonusTypes.RewardOverrideOptions>();
          const optionsFailed = !(optionsResponse.ok && optionsResponse.data);
          if (optionsResponse.ok && optionsResponse.data) {
            optionsResponse.data.forEach((entry) =>
              options.set(entry.rewardName, entry),
            );
          }

          const matchingSubPeriod = subPeriods.find(
            (sp) =>
              sp.startDate === actual.startDate &&
              sp.endDate === actual.endDate,
          );

          return {
            bonusDatasetRecordId,
            startDate: actual.startDate,
            endDate: actual.endDate,
            label: matchingSubPeriod?.label ?? actual.startDate,
            actual,
            persisted: overrideResponse.ok
              ? overrideResponse.data || undefined
              : undefined,
            options,
            optionsFailed,
            isQualified: actual.eligibleForBonus === 'qualified',
          };
        }),
      );
    };

    load().then((weeks) => {
      if (!isActive) {
        return;
      }
      setBaseWeeks(weeks);
      setLoadedKey(loadKey);
    });

    return () => {
      isActive = false;
    };
  }, [
    api,
    accountId,
    driver,
    bonusProgramId,
    intervalId,
    isActiveMode,
    hasSubPeriods,
    loadKey,
    subPeriods,
  ]);

  const stageWeekReward = useCallback(
    ({
      bonusDatasetRecordId,
      rewardName,
      override,
    }: {
      bonusDatasetRecordId: string;
      rewardName: string;
      override: BonusTypes.Overrides.UserInputRewardOverride | undefined;
    }) => {
      setStagedByRecord((prev) => {
        const others = (prev[bonusDatasetRecordId] ?? []).filter(
          (o) => o.rewardName !== rewardName,
        );
        const next = override ? [...others, override] : others;
        return { ...prev, [bonusDatasetRecordId]: next };
      });
    },
    [],
  );

  const stageWeekQualifier = useCallback(
    ({
      bonusDatasetRecordId,
      qualifier,
    }: {
      bonusDatasetRecordId: string;
      qualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
    }) => {
      setStagedQualifierByRecord((prev) => {
        if (!qualifier) {
          const { [bonusDatasetRecordId]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [bonusDatasetRecordId]: qualifier };
      });
    },
    [],
  );

  // Stage (or un-stage) the removal of a week's persisted criteria override.
  const stageWeekCriteriaClear = useCallback(
    ({
      bonusDatasetRecordId,
      criterionField,
      isCleared,
    }: {
      bonusDatasetRecordId: string;
      criterionField: string;
      isCleared: boolean;
    }) => {
      setStagedCriteriaClearsByRecord((prev) => {
        const others = (prev[bonusDatasetRecordId] ?? []).filter(
          (f) => f !== criterionField,
        );
        const next = isCleared ? [...others, criterionField] : others;
        return { ...prev, [bonusDatasetRecordId]: next };
      });
    },
    [],
  );

  // A signature of every week's staged set (rewards + qualifier + criteria
  // clears) so the preview effect re-runs only when a staged selection changes.
  const stagedSignature = useMemo(
    () =>
      JSON.stringify({
        stagedByRecord,
        stagedQualifierByRecord,
        stagedCriteriaClearsByRecord,
      }),
    [stagedByRecord, stagedQualifierByRecord, stagedCriteriaClearsByRecord],
  );

  // Live preview per changed week. Weeks with staged changes fetch a projected
  // reward output; reverted weeks clear their preview. WEL periods are ≤4 weeks so
  // the parallel fan-out is cheap.
  useEffect(() => {
    if (!isActiveMode || !intervalId || baseWeeks.length === 0) {
      return;
    }

    let isActive = true;

    baseWeeks.forEach((week) => {
      const staged = stagedByRecord[week.bonusDatasetRecordId] ?? [];
      const stagedQualifier =
        stagedQualifierByRecord[week.bonusDatasetRecordId];
      const stagedCriteriaClears =
        stagedCriteriaClearsByRecord[week.bonusDatasetRecordId] ?? [];
      if (
        staged.length === 0 &&
        !stagedQualifier &&
        stagedCriteriaClears.length === 0
      ) {
        setPreviewByRecord((prev) =>
          prev[week.bonusDatasetRecordId] === undefined
            ? prev
            : { ...prev, [week.bonusDatasetRecordId]: undefined },
        );
        setPreviewLoadingByRecord((prev) =>
          prev[week.bonusDatasetRecordId]
            ? { ...prev, [week.bonusDatasetRecordId]: false }
            : prev,
        );
        return;
      }

      // PUT /preview is a dry run of the PUT /overrides REPLACE: send the week's
      // complete desired state (the same one the confirm sends).
      const state = buildWeekOverrideState({
        week,
        staged,
        stagedQualifier,
        stagedCriteriaClears,
      });

      setPreviewLoadingByRecord((prev) => ({
        ...prev,
        [week.bonusDatasetRecordId]: true,
      }));

      putBonusPreview({
        api,
        ...state,
        bonusDatasetRecordId: week.bonusDatasetRecordId,
        bonusProgramId,
        driver,
        intervalId,
      }).then((response) => {
        if (!isActive) {
          return;
        }
        if (response.ok && response.data) {
          setPreviewByRecord((prev) => ({
            ...prev,
            [week.bonusDatasetRecordId]: response.data,
          }));
        }
        // Always clear loading so Confirm can't hang on a rejected preview.
        setPreviewLoadingByRecord((prev) => ({
          ...prev,
          [week.bonusDatasetRecordId]: false,
        }));
      });
    });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    api,
    bonusProgramId,
    driver,
    intervalId,
    isActiveMode,
    stagedSignature,
    baseWeeks,
  ]);

  const weeks = useMemo((): WeekOverrideState[] => {
    return baseWeeks.map((week) => {
      const stagedQualifier =
        stagedQualifierByRecord[week.bonusDatasetRecordId];
      const persistedQualifier = week.persisted?.qualifierOverride ?? undefined;
      const effectiveQualifier = buildEffectiveQualifier({
        persistedQualifier,
        stagedQualifier,
      });
      return {
        ...week,
        staged: stagedByRecord[week.bonusDatasetRecordId] ?? [],
        stagedQualifier,
        persistedQualifier,
        stagedCriteriaClears:
          stagedCriteriaClearsByRecord[week.bonusDatasetRecordId] ?? [],
        isEffectivelyQualified: effectiveQualifier
          ? effectiveQualifier.value
          : week.isQualified,
        preview: previewByRecord[week.bonusDatasetRecordId],
        isPreviewLoading: !!previewLoadingByRecord[week.bonusDatasetRecordId],
      };
    });
  }, [
    baseWeeks,
    stagedByRecord,
    stagedQualifierByRecord,
    stagedCriteriaClearsByRecord,
    previewByRecord,
    previewLoadingByRecord,
  ]);

  // A week is "changed" when it has any staged reward/qualifier override or a
  // staged criteria-override removal.
  const isWeekChanged = (w: WeekOverrideState) =>
    w.staged.length > 0 ||
    !!w.stagedQualifier ||
    w.stagedCriteriaClears.length > 0;

  const byRecordId = useMemo(() => {
    const map = new Map<string, WeekOverrideState>();
    weeks.forEach((week) => map.set(week.bonusDatasetRecordId, week));
    return map;
  }, [weeks]);

  const changedWeekCount = useMemo(
    () => weeks.filter(isWeekChanged).length,
    [weeks],
  );

  const stagedOverrideCount = useMemo(
    () =>
      weeks.reduce(
        (sum, w) =>
          sum +
          w.staged.length +
          (w.stagedQualifier ? 1 : 0) +
          w.stagedCriteriaClears.length,
        0,
      ),
    [weeks],
  );

  const cumulativeOverrideCount = useMemo(
    () =>
      weeks.reduce((sum, w) => {
        const rewardCount = buildEffectiveRewardOverrides(
          w.actual.rewardOverrides,
          w.staged,
        ).length;
        const qualifierCount = buildEffectiveQualifier({
          persistedQualifier: w.persistedQualifier,
          stagedQualifier: w.stagedQualifier,
        })
          ? 1
          : 0;
        return sum + rewardCount + qualifierCount;
      }, 0),
    [weeks],
  );

  const stagedRemovalCount = useMemo(
    () =>
      weeks.reduce(
        (sum, w) =>
          sum +
          w.staged.filter((s) => s.action === 'Clear').length +
          (w.stagedQualifier?.action === 'Clear' ? 1 : 0) +
          w.stagedCriteriaClears.length,
        0,
      ),
    [weeks],
  );

  const isAnyPreviewLoading = useMemo(
    () => weeks.some((w) => w.isPreviewLoading),
    [weeks],
  );

  const isNoteRequired = useMemo(
    () =>
      weeks.some(
        (w) =>
          w.staged.some((s) => s.action === 'Set') ||
          w.stagedQualifier?.action === 'Set',
      ),
    [weeks],
  );

  const aggregatePayChange = useMemo(() => {
    const changed = weeks.filter(isWeekChanged);
    if (changed.length === 0) {
      return undefined;
    }
    const actualPay = changed.reduce((sum, w) => sum + w.actual.totalReward, 0);
    const previewPay = changed.reduce(
      (sum, w) => sum + (w.preview?.totalReward ?? w.actual.totalReward),
      0,
    );
    return { actualPay, previewPay };
  }, [weeks]);

  // Per-week breakdown of every changed week: each reward the account keeps
  // visible while overriding (BonusOverridesModal), in programme order. Rewards
  // the override moves show actual → preview; the rest show their current value.
  // Raw values — the dialog formats them.
  const breakdown = useMemo((): WeeklyWeekBreakdown[] => {
    if (!bonusProgram) {
      return [];
    }
    const visibleRewardFields = bonusProgram.rewardFields.filter(
      (rf) => !hiddenRewardNames.has(rf.name),
    );

    return weeks
      .filter(isWeekChanged)
      .map((w): WeeklyWeekBreakdown => {
        const changes = visibleRewardFields.map((rf): WeeklyPayChange => {
          const actual = findRewardValue(w.actual.rewards, rf.name) ?? 0;
          const preview = w.preview
            ? findRewardValue(w.preview.rewards, rf.name) ?? actual
            : actual;
          return {
            rewardName: rf.name,
            label: rf.displayName || startCase(rf.name),
            field: rf.rewardValueField,
            actual,
            preview,
            isChanged: preview !== actual,
          };
        });
        return { startDate: w.startDate, label: w.label, changes };
      })
      .filter((w) => w.changes.length > 0);
  }, [weeks, bonusProgram, hiddenRewardNames]);

  const onWeeklyOverridesConfirmed = useCallback(
    async (
      note: string,
      reasonCodes: string[],
    ): Promise<WeeklyConfirmResult> => {
      if (!intervalId) {
        return { ok: false, total: 0, succeeded: 0, failed: [] };
      }

      const changed = weeks.filter(isWeekChanged);

      const results = await Promise.allSettled(
        changed.map(async (week) => {
          const bonusDatasetRecordId = week.bonusDatasetRecordId;
          // The full REPLACE state = persisted ⊕ staged for every kind.
          const state = buildWeekOverrideState({
            week,
            staged: week.staged,
            stagedQualifier: week.stagedQualifier,
            stagedCriteriaClears: week.stagedCriteriaClears,
          });

          if (!hasAnyOverride(state)) {
            const response = await deleteDriverOverrides({
              api,
              driver,
              bonusProgramId,
              intervalId,
              bonusDatasetRecordId,
            });
            if (!response.ok && response.status !== 404) {
              throw new Error('delete failed');
            }
            return;
          }

          const body = toOverridesRequest({
            state,
            comment: note,
            reasonCodes,
          });
          const response = await putDriverOverrides({
            api,
            driver,
            bonusProgramId,
            intervalId,
            bonusDatasetRecordId,
            body,
          });
          if (!response.ok) {
            throw new Error('put failed');
          }
        }),
      );

      const failed = changed.filter(
        (_week, i) => results[i].status === 'rejected',
      );
      const succeeded = changed.filter(
        (_week, i) => results[i].status === 'fulfilled',
      );

      // Drop staged state for weeks that saved so a retry only re-sends failures.
      if (succeeded.length > 0) {
        setStagedByRecord((prev) => {
          const next = { ...prev };
          succeeded.forEach((w) => delete next[w.bonusDatasetRecordId]);
          return next;
        });
        setStagedQualifierByRecord((prev) => {
          const next = { ...prev };
          succeeded.forEach((w) => delete next[w.bonusDatasetRecordId]);
          return next;
        });
        setStagedCriteriaClearsByRecord((prev) => {
          const next = { ...prev };
          succeeded.forEach((w) => delete next[w.bonusDatasetRecordId]);
          return next;
        });
      }

      if (failed.length === 0) {
        showToast('Overrides Updated');
        exitOverrideMode();
        onOverridesSaved?.();
        return {
          ok: true,
          total: changed.length,
          succeeded: succeeded.length,
          failed: [],
        };
      }

      showToast(
        `Saved ${succeeded.length} of ${changed.length} week(s); failed: ${failed
          .map((w) => w.label)
          .join(', ')}`,
      );
      onOverridesSaved?.();
      return {
        ok: false,
        total: changed.length,
        succeeded: succeeded.length,
        failed: failed.map((w) => ({ startDate: w.startDate, label: w.label })),
      };
    },
    [
      api,
      driver,
      bonusProgramId,
      intervalId,
      weeks,
      showToast,
      exitOverrideMode,
      onOverridesSaved,
    ],
  );

  const value = useMemo(
    (): WeeklyBonusOverrideContextType => ({
      isLoading,
      weeks,
      byRecordId,
      stageWeekReward,
      stageWeekQualifier,
      stageWeekCriteriaClear,
      changedWeekCount,
      stagedOverrideCount,
      cumulativeOverrideCount,
      stagedRemovalCount,
      isAnyPreviewLoading,
      aggregatePayChange,
      breakdown,
      isNoteRequired,
      onWeeklyOverridesConfirmed,
      bonusProgram,
    }),
    [
      isLoading,
      weeks,
      byRecordId,
      stageWeekReward,
      stageWeekQualifier,
      stageWeekCriteriaClear,
      changedWeekCount,
      stagedOverrideCount,
      cumulativeOverrideCount,
      stagedRemovalCount,
      isAnyPreviewLoading,
      aggregatePayChange,
      breakdown,
      isNoteRequired,
      onWeeklyOverridesConfirmed,
      bonusProgram,
    ],
  );

  return (
    <WeeklyBonusOverrideContext.Provider value={value}>
      {children}
    </WeeklyBonusOverrideContext.Provider>
  );
};

export default WeeklyBonusOverrideProvider;
